import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {NotificationManager} from 'react-notifications';

import {useApi} from '../../lib/api';
import SideModal from '../../components/SideModal';
import Form from '../../components/Form';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Loading from '../../components/Loading';
import {Checkbox} from '../FormBuilderScreen/QuestionBuilder';
import {alphaSort} from '../../lib/sort';
import search from '../../lib/search';
import {getScaleTarget} from "../FormBuilderScreen/shared";

function AttachTemplatesModal({layout, open, templates = [], templatesLoading = false, onClose, onSaved}) {
  const api = useApi();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [initialIds, setInitialIds] = useState(new Set());
  const [filter, setFilter] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!layout) return;
    setFilter('');
    const initial = new Set(
      templates
        .filter(t => t.labelLayoutId === layout.id)
        .map(t => t.id)
    );
    setInitialIds(initial);
    setSelectedIds(new Set(initial));
  }, [layout, templates]);

  const loading = templatesLoading;

  const toggleTemplate = (templateId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(templateId)) {
        next.delete(templateId);
      } else {
        next.add(templateId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!layout) return;

    const toAttach = [...selectedIds].filter(id => !initialIds.has(id));
    const toDetach = [...initialIds].filter(id => !selectedIds.has(id));

    if (toAttach.length === 0 && toDetach.length === 0) {
      onClose();
      return;
    }

    setSaving(true);

    const updates = [
      ...toAttach.map(id => ({id, labelLayoutId: layout.id})),
      ...toDetach.map(id => ({id, labelLayoutId: null}))
    ];

    const failures = [];
    for (const {id, labelLayoutId} of updates) {
      try {
        const fullTemplate = await api.getTemplate(id);
        await api.editTemplate({...fullTemplate, labelLayoutId});
      } catch (e) {
        failures.push(id);
      }
    }

    setSaving(false);

    // If it fails to update all templates do not refresh the templates list
    if (failures.length === updates.length) {
      NotificationManager.error('Failed to update template assignments');
      onClose();
      return;
    }

    // Show error for ones that failed to update, but still call onSaved to update the templates list
    if (failures.length > 0) {
      NotificationManager.error(`Updated ${updates.length - failures.length} of ${updates.length} templates. ${failures.length} failed.`);
    } else {
      NotificationManager.success(`Updated template assignments for ${layout.name}`);
    }
    onSaved();
  };

  const eligibleTemplates = templates
    .filter(t => t.current === true) // only unarchived forms
    .filter(t => (t.questions || []).some(getScaleTarget) || t.labelLayoutId !== null);
  // If it has a label layout, we want the users to be able to unselect them

  const filteredTemplates = eligibleTemplates
    .filter(t => search(t.name, filter))
    .sort(alphaSort('asc', t => t.name));

  const eligibleIds = new Set(eligibleTemplates.map(t => t.id));
  const selectedEligibleCount = [...selectedIds].filter(id => eligibleIds.has(id)).length;

  const changedCount =
    [...selectedIds].filter(id => !initialIds.has(id)).length +
    [...initialIds].filter(id => !selectedIds.has(id)).length;

  return (
    <Form onSubmit={handleSave}>
      <SideModal
        open={open}
        title={layout ? `${layout.name}` : 'Attach Templates'}
        onClose={saving ? () => {} : onClose}
        options={[
          <Button key="save" type="submit" disabled={saving || loading || changedCount === 0}>
            {saving ? 'Saving...' : changedCount > 0 ? `Save (${changedCount} change${changedCount === 1 ? '' : 's'})` : 'Save'}
          </Button>,
          <Button key="cancel" tertiary onClick={onClose} disabled={saving}>Cancel</Button>
        ]}
      >
        <Content>
          {loading ? (
            <Loading />
          ) : (
            <>
              <Description>
                Select the forms that should use this print layout.
                Saving will update each form's printer label assignment.
                <br />
                <br />
                Only forms with questions set as scale targets can be attached to this print layout.
              </Description>
              <Input
                id="attach-templates-filter"
                type="text"
                placeholder="Search forms..."
                value={filter}
                onChange={e => setFilter(e.target.value)}
              />
              <SelectionSummary>
                {selectedEligibleCount} of {eligibleTemplates.length} selected
              </SelectionSummary>
              <TemplateList>
                {filteredTemplates.length === 0 ? (
                  <EmptyMessage>
                    {templates.length === 0 ? 'No forms available.' : 'No forms match your search.'}
                  </EmptyMessage>
                ) : (
                  filteredTemplates.map(t => (
                    <Checkbox key={t.id}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(t.id)}
                        onChange={() => toggleTemplate(t.id)}
                        disabled={saving}
                      />
                      &nbsp;
                      {t.name}
                    </Checkbox>
                  ))
                )}
              </TemplateList>
            </>
          )}
        </Content>
      </SideModal>
    </Form>
  );
}

const Content = styled(SideModal.Content)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 24rem;
`;

const Description = styled.p`
  margin: 0;
  color: #555;
  font-size: 0.875rem;
`;

const SelectionSummary = styled.div`
  font-size: 0.85rem;
  color: #747474;
`;

const TemplateList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 60vh;
  overflow-y: auto;
`;

const EmptyMessage = styled.div`
  color: #747474;
  font-size: 0.9rem;
  font-style: italic;
`;

export default AttachTemplatesModal;